import React, { useEffect, useState, useCallback } from 'react'
import { object, string, func, array, bool, oneOfType } from 'prop-types'
import { Alert } from '@livechat/design-system-react-components'

import { LocalImage } from '@/components/LocalImage'
import Tabs from '../Tabs'
import {
  wrapperCss,
  headerCss,
  iconCss,
  nameCss,
  descriptionWrapperCss,
  descriptionCss,
  actionCss,
  additionalInfoCss,
  alertCss,
} from './Header.style'

const Header = ({
  icons,
  logo,
  description,
  name,
  additionalInfo,
  activeTab,
  actionBtn,
  tabs,
  innerRef,
  className,
  dataTest,
  underMaintenance,
}) => {
  const [isCompact, setIsCompact] = useState(false)

  const handleScroll = useCallback(() => {
    const position = window.scrollY
    const localIsCompact = position > 0
    if (isCompact !== localIsCompact) {
      setIsCompact(localIsCompact)
    }
  }, [isCompact])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  const dataTests = {
    header: dataTest ? `${dataTest}-header` : null,
    name: dataTest ? `${dataTest}-name` : null,
    icon: dataTest ? `${dataTest}-icon` : null,
    description: dataTest ? `${dataTest}-description` : null,
    actionBtn: dataTest ? `${dataTest}-action-btn` : null,
  }

  return (
    <section
      css={wrapperCss}
      ref={innerRef}
      className={className}
      data-test={dataTests.header}
    >
      {underMaintenance && (
        <Alert kind="info" css={alertCss} data-test="under-maintenance-alert">
          This application will be <strong>available soon</strong>. Stay tuned
          for updates!
        </Alert>
      )}
      <div css={headerCss}>
        <div data-test={dataTests.icon} css={iconCss(isCompact)}>
          <LocalImage
            image={logo}
            fallbackUrl={icons?.small || icons?.original}
            fallback="fallbackThumbnail"
            alt={name}
          />
        </div>

        <h1 data-test={dataTests.name} css={nameCss(isCompact)}>
          {name}
        </h1>

        <div
          data-test={dataTests.description}
          css={descriptionWrapperCss(isCompact)}
        >
          <p css={descriptionCss(isCompact)}>{description}</p>
          {additionalInfo && (
            <div css={additionalInfoCss(isCompact)}>
              {additionalInfo(isCompact)}
            </div>
          )}
        </div>

        <div
          data-test={dataTests.actionBtn}
          css={actionCss}
          id="action-btn-app"
        >
          {!!actionBtn &&
            (typeof actionBtn === 'function'
              ? actionBtn(isCompact)
              : actionBtn)}
        </div>
      </div>

      <Tabs activeTab={activeTab} tabs={tabs} />
    </section>
  )
}

Header.propTypes = {
  icons: object,
  logo: object,
  description: string,
  name: string,
  activeTab: string,
  innerRef: object,
  className: string,
  dataTest: string,
  actionBtn: oneOfType([object, func]),
  additionalInfo: func,
  tabs: array,
  isExpert: bool,
  underMaintenance: bool,
}

export default React.forwardRef((props, ref) => (
  <Header innerRef={ref} {...props} />
))
